/**
 * livekitKeyWrap.js — Sealed-Sender wrap for LiveKit SFU room
 * secrets.
 *
 * The 32-byte AES-GCM value that seeds the SFrame ratchet never
 * transits in plaintext. The initiator generates it locally
 * (see randomRoomKey in utils/livekitKeyProvider) and this module
 * wraps it separately for each invited participant using their
 * long-term RSA-OAEP public key — the same primitive the main
 * messaging channel uses for Sealed-Sender envelopes.
 *
 * Why a thin dedicated wrapper (and not `encryptMessage` from
 * AuthContext): `encryptMessage` produces a *message-shaped*
 * envelope (AES-GCM content + per-peer wrapped AES keys + view-
 * once metadata + Dilithium signature) that's too heavy for a
 * 32-byte payload travelling over the WS `call_signal` channel.
 * Here we want one round-trip per peer: wrap 32 bytes with their
 * RSA-OAEP pubkey → base64 → done.
 *
 * PQ hybrid layer: NOT added in Phase 3. The classical RSA-OAEP
 * wrap is sufficient defence-in-depth given the room key is
 * already short-lived (TTL = call duration) and the SFU itself
 * provides an additional layer of SFrame key ratcheting. A
 * Kyber-hybrid upgrade is a 20-line additive change that can
 * land when the rest of the app's PQ story hardens.
 */

/**
 * Wrap a 32-byte LiveKit shared secret for a single peer.
 *
 * @param {Uint8Array} sharedKeyBytes  Exactly 32 bytes.
 * @param {Object}     peerPublicKeyJwk RSA-OAEP JWK from
 *                     `GET /api/users/{id}` (.public_key field).
 * @returns {Promise<string>} base64 ciphertext (RSA-OAEP output).
 *
 * Throws if the key length is wrong, or if the JWK is malformed.
 * Callers should fetch the peer's JWK once and reuse the imported
 * CryptoKey if wrapping for many peers — but for small groups
 * (N ≤ 10) the per-wrap PKI import overhead is negligible.
 */
export const wrapSharedKeyForPeer = async (sharedKeyBytes, peerPublicKeyJwk) => {
  if (!(sharedKeyBytes instanceof Uint8Array) || sharedKeyBytes.byteLength !== 32) {
    throw new Error('livekitKeyWrap: sharedKeyBytes must be 32-byte Uint8Array');
  }
  if (!peerPublicKeyJwk) {
    throw new Error('livekitKeyWrap: peerPublicKeyJwk required');
  }
  const jwk = typeof peerPublicKeyJwk === 'string'
    ? JSON.parse(peerPublicKeyJwk)
    : peerPublicKeyJwk;
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
  const ct = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    sharedKeyBytes,
  );
  return _b64encode(new Uint8Array(ct));
};

/**
 * Unwrap a wrapped 32-byte LiveKit shared secret with the
 * recipient's private key.
 *
 * @param {string}   ciphertextB64  Output of wrapSharedKeyForPeer.
 * @param {CryptoKey} privateKey    User's imported RSA-OAEP private
 *                                  key (from AuthContext.cryptoKeys.privateKey).
 * @returns {Promise<Uint8Array>} 32 raw bytes, ready to feed into
 *                                `installPassphrase()`.
 */
export const unwrapSharedKey = async (ciphertextB64, privateKey) => {
  if (!ciphertextB64 || typeof ciphertextB64 !== 'string') {
    throw new Error('livekitKeyWrap: ciphertextB64 required');
  }
  if (!privateKey) {
    throw new Error('livekitKeyWrap: privateKey required');
  }
  const ct = _b64decode(ciphertextB64);
  const pt = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    ct,
  );
  const out = new Uint8Array(pt);
  if (out.byteLength !== 32) {
    throw new Error(`livekitKeyWrap: unwrapped ${out.byteLength} bytes, expected 32`);
  }
  return out;
};

/**
 * Base64 helpers. Bespoke rather than reaching for a library —
 * the two call sites are both in this file and the input is
 * always binary-safe bytes.
 */
const _b64encode = (bytes) => {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
};

const _b64decode = (str) => {
  const bin = atob(str);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};
